import { landmineNames } from '@mm/constants/landmine-names';
import { useCompanyCRUDActions } from '@mm/state/actions/CompanyCRUDActions';
import modalActions from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import Hint from '../../../../common/components/Hint/Hint';
import ImageUploader from '../../../../common/components/ImageUploader/ImageUploader';
import {
  BadgeTheme,
  MonkeyBadge,
} from '../../../../common/components/MonkeyBadge/MonkeyBadge';
import PaneSection from '../../../../common/components/PaneSection/PaneSection';
import TextInput from '../../../../common/components/TextInput';
import './CompanyPanel.pcss';

export const CompanyPanel = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  const activeCompany = useSelector(activeCompanySelector);
  const activeCompanyId = activeCompany?.id;
  const activeCompanyName = activeCompany?.name;
  const activeCompanyEditable = activeCompany?.editable;

  const canUploadCompanyLogo = useSelector(
    combinedFeatureFlagsSelector,
  ).settingsCompanyLogoUpload;
  const activeCompanyLogo = activeCompany?.logo?.urls?.medium || null;

  const [companyName, setCompanyName] = useState(activeCompanyName);
  const [hasPermission, setHasPermission] = useState(activeCompanyEditable);

  const { update } = useCompanyCRUDActions();

  const onCompanyNameUpdate = () => {
    if (companyName && companyName !== activeCompanyName) {
      update({ ...activeCompany, name: companyName });
    }
  };

  useEffect(() => {
    setCompanyName(activeCompanyName);
    setHasPermission(activeCompanyEditable);
  }, [activeCompanyId, activeCompanyName, activeCompanyEditable]);

  const updateCompanyAssetId = (assetId: number | null) => {
    update({ ...activeCompany, assetId });
  };

  const onCompanyLogoUpload = (assetId: number) =>
    updateCompanyAssetId(assetId);

  const onRemove = () => updateCompanyAssetId(null);

  const promptTeamPlanIfNeeded = () =>
    !canUploadCompanyLogo &&
    dispatch(
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['SETTINGS__COMPANY-PANEL'],
        feature: 'settingsCompanyLogoUpload',
      }),
    );

  return (
    <div className="company-panel">
      <h1 className="settings-pane__title">Company</h1>

      <PaneSection title="Company name" className="company-panel__pane">
        <div className="company-panel__pane-content">
          <TextInput
            value={companyName}
            disabled={!hasPermission}
            onChange={e => setCompanyName(e.target.value)}
            onBlur={onCompanyNameUpdate}
            onMouseLeave={onCompanyNameUpdate}
            maxLength={50}
          />
        </div>
      </PaneSection>

      <PaneSection
        title={
          <span className="company-panel__logo-title-container">
            Company logo for signup page
            {!canUploadCompanyLogo && (
              <MonkeyBadge
                className="company-panel__team-badge"
                theme={BadgeTheme.greenGradient}
              >
                AGENCY
              </MonkeyBadge>
            )}
          </span>
        }
        className="company-panel__pane"
      >
        <div
          className="company-panel__pane-content company-panel__pane-content-logo"
          onClick={promptTeamPlanIfNeeded}
        >
          <ImageUploader
            disabled={!canUploadCompanyLogo || !hasPermission}
            file={activeCompanyLogo}
            className="company-panel__logo-form"
            onUpload={onCompanyLogoUpload}
            disableCrop={true}
            withRemoveBtn={true}
            onRemove={onRemove}
          />
          <Hint className="company-panel__hint">
            <span className="company-panel__hint-text">
              Supported formats: JPG, JPEG, PNG, GIF
              <br />
              Recommended dimensions: 250x130px / 500x260px
              <br />
            </span>
          </Hint>
        </div>
      </PaneSection>
    </div>
  );
};
